import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button, Card, StatusPill, Modal } from "@/components/kit";

const tone = (s) => (s >= 90 ? "green" : s >= 80 ? "gold" : "maroon");

function Who({ name, email, sub }) {
  return (
    <div className="min-w-0">
      <div className="text-[15px] font-semibold truncate">{name || "—"} {sub && <span className="font-mono text-[12px] text-ink/45">{sub}</span>}</div>
      <div className="text-[13px] text-ink/60 truncate">{email || "no email"}</div>
    </div>
  );
}

function Section({ title, hint, count, children, testid }) {
  return (
    <Card className="mb-6 overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-ink/10">
        <h2 className="font-zilla text-xl font-semibold">{title} <span className="text-ink/45 text-base">({count})</span></h2>
        {hint && <p className="text-[14px] text-ink/60 mt-0.5">{hint}</p>}
      </div>
      {count === 0 ? <div className="p-6 text-center text-ink/50 text-[15px]">Nothing here.</div> : children}
    </Card>
  );
}

function PairRow({ id, checked, onToggle, member, user, score, testid }) {
  return (
    <label data-testid={testid} className="flex items-center gap-4 px-5 py-3 border-b border-ink/8 last:border-0 cursor-pointer hover:bg-ink/[0.03]">
      <input type="checkbox" className="h-5 w-5 accent-maroon" checked={checked} onChange={() => onToggle(id)} data-testid={`${testid}-check`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1 min-w-0">
        <Who name={member.name} email={member.email} sub={member.member_number} />
        <Who name={user.name} email={user.email} sub="login" />
      </div>
      {score != null && <StatusPill tone={tone(score)} data-testid={`${testid}-score`}>{score}% match</StatusPill>}
    </label>
  );
}

function SimpleList({ rows, kind }) {
  return rows.map((r) => (
    <div key={r.member_id || r.user_id} className="px-5 py-3 border-b border-ink/8 last:border-0 flex items-center justify-between gap-3" data-testid={`${kind}-${r.member_id || r.user_id}`}>
      <Who name={r.name} email={r.email} sub={r.member_number} />
      {r.account_type && <StatusPill>{r.account_type.replace("_", " ")}</StatusPill>}
    </div>
  ));
}

export default function AccountLinks() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => { const r = await api.get("/membership/account-links/preview"); setData(r.data); setSel({}); };
  useEffect(() => { load().catch(() => toast.error("Couldn't load the preview")); }, []);

  if (!data) return <div className="p-10 text-ink/55" data-testid="account-links-loading">Building the dry run…</div>;

  const toggle = (id) => setSel((s) => {
    const n = { ...s };
    if (n[id]) { delete n[id]; return n; }
    const [mid] = id.split("|");
    Object.keys(n).forEach((k) => k.startsWith(`${mid}|`) && delete n[k]);
    n[id] = true;
    return n;
  });
  const chosen = Object.keys(sel).map((k) => { const [member_id, user_id, source] = k.split("|"); return { member_id, user_id, source }; });
  const allEmail = () => setSel((s) => { const n = { ...s }; data.email_matches.forEach((r) => (n[`${r.member.member_id}|${r.user.user_id}|email`] = true)); return n; });

  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post("/membership/account-links/apply", { links: chosen });
      toast.success(`Linked ${r.data.applied.length} account(s)${r.data.skipped.length ? `, skipped ${r.data.skipped.length}` : ""}`);
      r.data.skipped.forEach((s) => toast.error(s.reason));
      setConfirm(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't apply links"); }
    setBusy(false);
  };

  return (
    <div className="max-w-5xl pb-28" data-testid="account-links-page">
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1">Link Accounts</h1>
      <p className="text-[16px] text-ink/70 mb-6">A dry run — nothing changes until you tick rows and press <b>Apply selected</b>. Every link is written to the audit log.</p>

      <Section testid="list-email" title="A · Exact email matches" count={data.email_matches.length} hint="The login and the member record share the same email (ignoring case and spaces).">
        <div className="px-5 py-2 border-b border-ink/8"><Button variant="ghost" onClick={allEmail} data-testid="select-all-email">Select all email matches</Button></div>
        {data.email_matches.map((r) => {
          const id = `${r.member.member_id}|${r.user.user_id}|email`;
          return <PairRow key={id} id={id} checked={!!sel[id]} onToggle={toggle} member={r.member} user={r.user} testid={`email-${r.member.member_id}`} />;
        })}
      </Section>

      <Section testid="list-name" title="B · Possible name matches" count={data.name_matches.length} hint="Emails differ, names look alike. Review carefully. Next Gen Youth and suspended members are never suggested.">
        {data.name_matches.map((r) => r.candidates.map((c) => {
          const id = `${r.member.member_id}|${c.user.user_id}|name`;
          return <PairRow key={id} id={id} checked={!!sel[id]} onToggle={toggle} member={r.member} user={c.user} score={c.score} testid={`name-${r.member.member_id}-${c.user.user_id}`} />;
        }))}
      </Section>

      <Section testid="list-review" title="Needs review" count={data.needs_review.length} hint="One email is shared by several records or logins, so nothing is linked automatically. Fix these from the member record.">
        {data.needs_review.map((r) => (
          <div key={r.email} className="px-5 py-3 border-b border-ink/8 last:border-0" data-testid={`review-${r.email}`}>
            <div className="text-[15px] font-semibold">{r.email}</div>
            <div className="text-[13px] text-ink/60">{r.reason}: {r.members.map((m) => `${m.name} (${m.member_number})`).join(", ")} · logins: {r.users.map((u) => u.name).join(", ")}</div>
          </div>
        ))}
      </Section>

      <Section testid="list-no-match" title="Members with no match" count={data.members_no_match.length} hint="Member records with no login and no suggested match.">
        <SimpleList rows={data.members_no_match} kind="nomatch" />
      </Section>

      <Section testid="list-no-member" title="Logins with no member record" count={data.logins_no_member.length} hint="Accounts that have signed up but aren't tied to any membership.">
        <SimpleList rows={data.logins_no_member} kind="nologin" />
      </Section>

      <div className="fixed bottom-0 inset-x-0 z-30 pointer-events-none">
        <div className="max-w-5xl mx-auto px-4 pb-4 flex justify-end">
          <Button className="pointer-events-auto shadow-lg" disabled={!chosen.length} onClick={() => setConfirm(true)} data-testid="apply-selected-btn">
            Apply selected ({chosen.length})
          </Button>
        </div>
      </div>

      <Modal open={confirm} onClose={() => setConfirm(false)} title="Link these accounts?" data-testid="apply-confirm-modal"
        footer={<><Button variant="secondary" onClick={() => setConfirm(false)} data-testid="apply-cancel-btn">Cancel</Button>
          <Button onClick={apply} disabled={busy} data-testid="apply-confirm-btn">{busy ? "Linking…" : `Link ${chosen.length}`}</Button></>}>
        <p className="text-[15px] text-ink/75">{chosen.filter((c) => c.source === "email").length} by email and {chosen.filter((c) => c.source === "name").length} by name will be linked. Each one is recorded in the audit log.</p>
      </Modal>
    </div>
  );
}
